/**
 * The masthead, which has to say something true about whichever county is open.
 *
 * Two things vary, and both would otherwise be false somewhere:
 *
 * 1. **The headline.** Palm Beach's rows are buildings on a milestone
 *    register and nothing else; it has no parcels behind them. A county with
 *    no buildings must not be headlined "0 buildings look past due", so the
 *    sentence follows what the county actually holds rather than being fixed.
 * 2. **The metrics.** A county that publishes no lien feed has no liens *to
 *    us*, which is not the same as none existing. Those tiles are dropped
 *    rather than shown as zero — see `signals` on /api/counties.
 */

import type { County, Status } from "../api/types";
import type { Filters, View } from "../lib/filters";
import { countyName } from "../lib/counties";
import { fmt, timestamp } from "../lib/format";
import { headline } from "../lib/headline";
import { reportQuery } from "../lib/reportParams";
import { REPORTS_PATH } from "../lib/route";
import { scoreScope } from "../lib/scoreScope";
import { hasSourceWarning, sourceWarning } from "../lib/sources";
import { Link } from "../router";

interface Props {
  status: Status | null;
  /** FIPS being viewed, "" for everything held. */
  county: string;
  /** The registry entry for that county, once loaded. */
  entry?: County;
  /** Every county the registry reports, so the masthead can name them. */
  counties?: County[];
  /**
   * What the table is showing, carried into the report as a starting point.
   *
   * Optional, because the masthead is rendered before a view is resolved on
   * some paths and an action that has to wait for filters is an action that
   * flickers. Absent, the link still works and lands on the county unfiltered.
   */
  view?: View;
  filters?: Filters;
}

export function Hero({ status, county, entry, counties, view, filters }: Props) {
  const stats = status?.stats;
  const lastPull = timestamp(status?.finished_at);
  const name = countyName(county);
  const where = name ? `${name} County` : coveredCounties(counties);

  // No entry means either "every county" or "not loaded yet"; both show the
  // full set rather than flashing a stripped-down masthead.
  const publishes = (flag: string) => (entry ? entry.signals.includes(flag) : true);
  const scope = scoreScope(entry?.signals);

  const buildings = stats?.buildings ?? 0;
  const title = headline("", stats, entry?.signals);

  return (
    <section className="hero">
      <div className="eyebrow">Open structural distress · {where}</div>
      {/* Derived from the county's signals, never written here: nine live
          counties roll up no buildings and publish no case feed, and this
          line used to tell every one of them its parcels carried an open
          case. See lib/headline.ts. */}
      <h1>
        {title.before}
        <em>{title.emphasis}</em>
        {title.after}
      </h1>
      <p>{copy(county)}</p>

      {/* Derived from the county's own signal list, never written per county:
          in six of the live counties every scored parcel carries
          the same score, and a score column that cannot rank must say so where
          the county is named rather than leave a subscriber sorting on a
          constant. See lib/scoreScope.ts. */}
      {scope.note && <p className="hero-caveat">{scope.note}</p>}

      {/* How old the feeds under these figures are.

          "Last pull" below says when *we* read the county; it says nothing
          about when the county last wrote, and the two are independent — Fort
          Lauderdale's code register stopped in 2019 and is fetched perfectly
          every morning, so a dashboard headline of unsafe-structure cases in
          Broward is a 2019 headline with today's timestamp beside it. The
          report has printed these dates since it shipped; this is the same
          sentence in the same words. See lib/sources.ts. */}
      {status && hasSourceWarning(status.sources) && (
        <p className="hero-caveat sources-caveat">
          {sourceWarning(status.sources)}
        </p>
      )}

      {/* The way out of the dashboard and into a document.
          
          In the masthead rather than in the filter rail, where it used to be,
          because it is not a filter: it is what somebody came here to produce,
          and the rail's controls are all things that change the table in place.
          It sits beside the county's own name for the same reason the report
          takes one — a report covers one county, and this is where the open
          one is stated.
          
          A link rather than a button, because the report page is a page: it
          has to be reachable by its URL, so the price it shows can be
          reloaded and sent to somebody else. The filters ride along as a
          starting point and are changeable there. */}
      <div className="heroaction">
        <Link
          to={`${REPORTS_PATH}${
            view && filters
              ? reportQuery(view, filters, county)
              : county
                ? `?county=${county}`
                : ""
          }`}
          className="btn"
        >
          Generate report
        </Link>
        <span className="sub">
          One county, as a dated PDF, priced per record.
        </span>
      </div>

      <div className="metastrip">
        {publishes("unsafe_structure") && (
          <Metric label="Unsafe structure" value={fmt(stats?.unsafe)} />
        )}
        {publishes("lien_recorded") && (
          <Metric label="Violation liens" value={fmt(stats?.liens)} />
        )}
        {publishes("homestead_lost") && (
          <Metric label="Homestead dropped" value={fmt(stats?.homestead_lost)} />
        )}
        {buildings > 0 && (
          <Metric label="Buildings" value={fmt(buildings)} />
        )}
        <Metric label="Parcels scored" value={fmt(stats?.units)} />
        <Metric
          label="Last pull"
          value={lastPull ?? (status?.running ? "running…" : "never")}
        />
      </div>
    </section>
  );
}

/**
 * The counties held, named from the registry rather than from a constant.
 *
 * This line used to read "Miami-Dade, Broward and Palm Beach", which was one
 * more copy of "which counties do we hold" than anyone would remember to edit
 * — and it went stale the day Lee landed, still naming three counties while
 * the map drew four. `/api/counties` already knows, so it answers.
 *
 * Falls back to a phrase that names none of them: before the registry loads
 * there is nothing to list, and a placeholder naming a subset would be wrong
 * rather than merely vague.
 */
export function coveredCounties(counties?: County[]): string {
  const names = (counties ?? [])
    .filter((entry) => entry.status === "live")
    .map((entry) => entry.name);
  const last = names.pop();
  if (last === undefined) return "every county we hold";
  if (names.length === 0) return last;
  return `${names.join(", ")} and ${last}`;
}

/**
 * Per-county, because the sentence that was true of Miami-Dade is not true of
 * the others: Palm Beach's buildings are not on the list for carrying
 * violations, and Broward's coverage is one city.
 *
 * Exported so a test can hold each live county to having its own sentence. A
 * county with no case here is not *wrong* — the default deliberately makes no
 * per-county claim and counts nothing — but it is silent about what that
 * county publishes, which is the thing this masthead exists to say.
 */
export function copy(county: string): string {
  switch (county) {
    case "12086":
      return (
        "Each one is three storeys or taller, over thirty years old, and " +
        "already carrying open violations in the county record. Ranked by how " +
        "much of the stack is affected, not by raw case count. Where " +
        "Miami-Dade has a recertification case on file we show its status; " +
        "where it does not, the milestone reading is our estimate and is " +
        "labelled as one."
      );
    case "12099":
      return (
        "Every building here is on Palm Beach County's own milestone " +
        "inspection register, so each status is a county record rather than " +
        "our estimate. The county publishes no code-enforcement or lien feed, " +
        "so those signals are absent rather than zero, and floor counts are " +
        "not published at all."
      );
    case "12011":
      return (
        "Broward coverage is the tax parcel roll Fort Lauderdale's permitting " +
        "system holds, which spans twelve municipalities: every residential " +
        "condominium parcel in it, scored on what the roll itself evidences. " +
        "Permit activity and Building Safety Inspection Program permits are " +
        "Fort Lauderdale's own and reach no other city; where the city has a " +
        "BSIP permit on file we show its status, and where it does not the " +
        "milestone reading is our estimate and is labelled as one. No code " +
        "register in the county is live — Fort Lauderdale's stopped in 2019 " +
        "and was withdrawn — and there is no lien feed, so those signals are " +
        "absent rather than zero."
      );
    case "12071":
      return (
        "Lee County's property appraiser publishes each building's storey " +
        "count and year built, so the milestone reading here rests on the " +
        "county's own description of the building rather than on a floor " +
        "count inferred from unit numbers. There is no milestone register, " +
        "so every status is our estimate and is labelled as one. The county's " +
        "only code feed records nuisance cases and the liens under them, and " +
        "it covers almost no condominiums — so those signals are absent on " +
        "these buildings rather than zero, and there is no permit feed at all."
      );
    default:
      return (
        "The counties publish different things, and the score is built only " +
        "from records that exist. Scores are comparable inside a county, not " +
        "across them — see the coverage map for what each one holds."
      );
  }
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="eyebrow">{label}</span>
      <b>{value}</b>
    </div>
  );
}
